import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  UserPlus,
  Users,
  Mail,
  Shield,
  Edit,
  Eye,
  Loader2,
  FolderOpen,
  Globe2,
} from "lucide-react";
import { GuestInvitationService } from "@/services/guestInvitationService";
import CreditConfirmationModal, {
  useCreditConfirmation,
} from "@/components/credits/CreditConfirmationModal";
import { JobType, getCreditCost } from "@/types/credits";
import { useUserSubscription } from "@/utils/subscriptionUtils";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import {
  getUserProjects,
  Project as ProjectType,
} from "@/services/projectService";

interface Project {
  id: string;
  name: string;
  type:
    | "environmental_analysis"
    | "carbon_tracking"
    | "prediction_model"
    | "custom";
  description?: string;
  created_at?: string;
}

interface InviteGuestModalProps {
  onInvitationSent?: () => void;
  preselectedProject?: ProjectType; // Allow pre-selecting a project
  preselectedProjectId?: string; // Alternative way to preselect
  isOpen?: boolean; // External control of modal state
  onClose?: () => void; // External close handler
}

export function InviteGuestModal({
  onInvitationSent,
  preselectedProject,
  preselectedProjectId,
  isOpen,
  onClose,
}: InviteGuestModalProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Use external control if provided, otherwise internal control
  const open = isOpen !== undefined ? isOpen : internalOpen;
  const setOpen =
    isOpen !== undefined
      ? (value: boolean) => {
          if (!value && onClose) {
            onClose();
          }
        }
      : setInternalOpen;

  // Debug logging
  useEffect(() => {
    console.log("InviteGuestModal props:", {
      isOpen,
      open,
      preselectedProject,
    });
  }, [isOpen, open, preselectedProject]);
  const [permissions, setPermissions] = useState({
    view: true,
    edit: false,
  });

  const { toast } = useToast();
  const { user } = useSupabaseAuth();
  const { tier } = useUserSubscription();
  const { modalState, requestConfirmation, closeModal, setModalOpen } =
    useCreditConfirmation();

  // Set preselected project when modal opens
  useEffect(() => {
    if (open) {
      if (preselectedProject) {
        setSelectedProject(preselectedProject.id);
      } else if (preselectedProjectId) {
        setSelectedProject(preselectedProjectId);
      }
    }
  }, [open, preselectedProject, preselectedProjectId]);

  // Load user's real projects
  useEffect(() => {
    const loadProjects = async () => {
      if (!user || !open) return;

      setLoadingProjects(true);
      try {
        // Fetch real user projects
        const userProjects = await getUserProjects(user.id);

        // Transform to the expected format
        const transformedProjects: Project[] = userProjects.map((project) => ({
          id: project.id,
          name: project.name,
          type: project.category
            .toLowerCase()
            .replace(/\s+/g, "_") as Project["type"],
          description: project.description,
        }));

        setProjects(transformedProjects);

        // If no projects found, provide a helpful message in the UI
        if (transformedProjects.length === 0) {
          console.log("No projects found for user");
        }
      } catch (error) {
        console.error("Error loading user projects:", error);
        toast({
          title: "Error",
          description: "Failed to load your projects. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [user, open]);

  const handleSendInvitation = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to send invitations.",
        variant: "destructive",
      });
      return;
    }

    if (!guestEmail.trim()) {
      toast({
        title: "Email Required",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedProject) {
      toast({
        title: "Project Required",
        description: "Please select a project for collaboration.",
        variant: "destructive",
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestEmail)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    // Use credit confirmation modal flow: request confirmation first,
    // then execute invitation. Credits are only consumed once the guest accepts.
    requestConfirmation(
      "GUEST_INVITE" as JobType,
      async () => {
        setLoading(true);
        try {
          const result = await GuestInvitationService.sendInvitation(
            user.id,
            guestEmail.trim(),
            selectedProject,
            permissions,
          );

          if (!result.success) {
            // Throw to trigger modal error handling and prevent credit deduction
            throw new Error(result.error || "Failed to send invitation");
          }

          const selectedProjectName =
            projects.find((p) => p.id === selectedProject)?.name ||
            selectedProject;
          if (result.emailSent === false) {
            toast({
              title: "Invitation created (email not sent)",
              description:
                result.invitationUrl
                  ? `We couldn't send the email automatically. Share this link with the guest: ${result.invitationUrl}`
                  : `We couldn't send the email automatically. Please share the invitation link manually.`,
              variant: "destructive",
            });
          } else {
            toast({
              title: "Invitation sent (or resent)",
              description: `Invitation is pending for ${guestEmail} — we'll charge 1 credit only if they accept. Project: ${selectedProjectName}`,
              variant: "default",
            });
          }

          // Reset form
          setGuestEmail("");
          setSelectedProject("");
          setPermissions({ view: true, edit: false });
          setOpen(false);

          // Notify parent component
          onInvitationSent?.();
        } finally {
          setLoading(false);
        }
      },
      {
        title: "Invite Guest",
        description: `This will invite ${guestEmail}. ${getCreditCost("GUEST_INVITE")} credit will be consumed only if they accept.`,
      },
    );
  };

  const getSubscriptionLimits = () => {
    switch (tier) {
      case "professional":
        return { maxInvites: 10, tierName: "Professional" };
      case "PAYG":
        return { maxInvites: null as number | null, tierName: "PAYG" };
      default:
        // Treat legacy or unknown tiers as PAYG (billed per-invite)
        return { maxInvites: null as number | null, tierName: "PAYG" };
    }
  };

  const limits = getSubscriptionLimits();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Only show trigger button if not externally controlled */}
      {isOpen === undefined && (
        <DialogTrigger asChild>
          <Button className="bg-gradient-to-r from-[#40e0d0] to-emerald-500 hover:from-[#35c5b5] hover:to-emerald-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl px-6 py-2.5 font-medium border-0">
            <UserPlus className="h-4 w-4 mr-2" />
            Invite Guest
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-lg bg-white dark:bg-gray-900 border-0 shadow-2xl rounded-2xl overflow-hidden">
        {/* Gradient Header */}
        <div className="relative bg-gradient-to-br from-[#40e0d0] via-[#35c5b5] to-emerald-500 px-6 py-8 -mx-6 -mt-6 mb-6">
          <div className="absolute inset-0 bg-gradient-to-br from-black/10 to-transparent" />
          <div className="relative">
            <DialogHeader>
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <div>
                  <DialogTitle className="text-2xl font-bold text-white">
                    Invite Team Member
                  </DialogTitle>
                  <DialogDescription className="text-white/80 text-sm mt-1">
                    Add a colleague to collaborate on this project
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {/* Plan Badge */}
            <div className="flex items-center gap-2 mt-4">
              <div className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-xs font-medium">
                {limits.tierName}
              </div>
              <div className="px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-xs">
                {limits.maxInvites === null
                  ? "Billed per invite — 1 credit each"
                  : `${limits.maxInvites} guests max`}
              </div>
            </div>
          </div>
        </div>

        {/* Modern Form */}
        <div className="space-y-6 px-6">
          {/* Project Selection */}
          <div className="space-y-3">
            <Label
              htmlFor="project-select"
              className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2"
            >
              <FolderOpen className="h-4 w-4 text-[#40e0d0]" />
              Select Project
            </Label>
            <Select
              value={selectedProject}
              onValueChange={setSelectedProject}
              disabled={loading || loadingProjects}
            >
              <SelectTrigger className="h-12 px-4 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-[#40e0d0] bg-gray-50 dark:bg-gray-800 transition-colors">
                <div className="flex items-center gap-2">
                  <Globe2 className="h-4 w-4 text-gray-400" />
                  <SelectValue
                    placeholder={
                      loadingProjects
                        ? "Loading projects..."
                        : "Choose a project to share"
                    }
                  />
                </div>
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    <div className="flex items-center gap-3 py-2">
                      <div
                        className={`w-3 h-3 rounded-full mr-3 ${
                          project.type === "environmental_analysis"
                            ? "bg-green-500"
                            : project.type === "carbon_tracking"
                              ? "bg-blue-500"
                              : project.type === "prediction_model"
                                ? "bg-purple-500"
                                : "bg-gray-500"
                        }`}
                      />
                      <div>
                        <div className="font-medium">{project.name}</div>
                      </div>
                    </div>
                  </SelectItem>
                ))}
                {projects.length === 0 && !loadingProjects && (
                  <SelectItem value="no-projects" disabled>
                    <div className="text-sm text-muted-foreground py-2">
                      No projects found. Create a project first to invite
                      collaborators.
                    </div>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {selectedProject && (
              <div className="text-xs text-gray-600 dark:text-gray-200 flex items-center gap-2">
                <Shield className="h-3 w-3" />
                Guest will have access to this specific project only
              </div>
            )}
          </div>

          {/* Email Input */}
          <div className="space-y-3">
            <Label
              htmlFor="guest-email"
              className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2"
            >
              <Mail className="h-4 w-4 text-[#40e0d0]" />
              Team Member Email
            </Label>
            <div className="relative">
              <Input
                id="guest-email"
                type="email"
                placeholder="colleague@company.com"
                value={guestEmail}
                onChange={(e) => setGuestEmail(e.target.value)}
                className="pl-11 pr-4 py-3 text-base border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:border-[#40e0d0] focus:ring-[#40e0d0] bg-gray-50 dark:bg-gray-800 transition-colors"
                disabled={loading}
              />
              <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          {/* Permissions Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-[#40e0d0]" />
              <Label className="text-base font-semibold text-gray-900 dark:text-white">
                Access Permissions
              </Label>
            </div>

            <div className="grid gap-4">
              {/* View Permission */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
                    <Eye className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      View Access
                    </div>
                    <div className="text-xs text-green-700 dark:text-green-400">
                      Can view project data and reports
                    </div>
                  </div>
                </div>
                <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-white" />
                </div>
              </div>

              {/* Edit Permission */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                    <Edit className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      Edit Access
                    </div>
                    <div className="text-xs text-blue-700 dark:text-blue-400">
                      Can modify project data and settings
                    </div>
                  </div>
                </div>
                <Switch
                  checked={permissions.edit}
                  onCheckedChange={(checked) =>
                    setPermissions((prev) => ({ ...prev, edit: checked }))
                  }
                  disabled={loading}
                  className="data-[state=checked]:bg-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2 pb-6">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
              className="flex-1 py-2.5 rounded-xl border-2 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendInvitation}
              disabled={loading || !guestEmail.trim()}
              className="flex-1 bg-gradient-to-r from-[#40e0d0] to-emerald-500 hover:from-[#35c5b5] hover:to-emerald-600 text-white py-2.5 rounded-xl shadow-lg disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Send Invitation
                </>
              )}
            </Button>
          </div>
        </div>
        {/* Credit Confirmation Modal (local to this invite flow) */}
        <CreditConfirmationModal
          open={modalState.open}
          onOpenChange={(open) => {
            if (!open) closeModal();
          }}
          jobType={modalState.jobType as any}
          userId={user?.id ?? ""}
          onConfirm={modalState.onConfirm || (async () => {})}
          title={modalState.title}
          description={modalState.description}
        />
      </DialogContent>
    </Dialog>
  );
}

export default InviteGuestModal;
